
import React from 'react';
import { motion } from "framer-motion";
import { StepHeader } from './StepHeader';
import { ChallengeStepContent } from './steps/ChallengeStepContent';
import { SkillsStepContent } from './steps/SkillsStepContent';
import { CareerProgressionStepContent } from './steps/CareerProgressionStepContent';
import { TutorialStep } from './tutorialSteps';
import { RoleSelectionPreview } from './steps/RoleSelectionPreview';
import { Separator } from '@/components/ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Card } from '@/components/ui/card';

interface StepContentProps {
  step: TutorialStep;
  stepIndex: number;
}

export const StepContent: React.FC<StepContentProps> = ({ step, stepIndex }) => {
  return (
    <motion.div 
      className="mt-8 space-y-6 min-h-[380px] flex flex-col"
      key={stepIndex}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      <StepHeader icon={step.icon} title={step.title} />
      <p className="text-lg text-white/80 text-center">{step.description}</p>
      
      {/* Detailed explanation */}
      {step.detailedContent && (
        <Card className="bg-web3-blue/30 border-0 p-4">
          <div 
            className="prose prose-invert max-w-none text-white/90 text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: step.detailedContent.replace(/\n\n/g, '<br/><br/>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}
          />
        </Card>
      )}
      
      {/* Step-specific interactive content */}
      {stepIndex === 1 && <RoleSelectionPreview />}
      {stepIndex === 2 && <ChallengeStepContent />}
      {stepIndex === 3 && <SkillsStepContent />}
      {stepIndex === 5 && <CareerProgressionStepContent />}
      
      {/* FAQ accordion for each step */}
      <Separator className="my-4" />
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="faq-1" className="border-web3-blue/30">
          <AccordionTrigger className="text-white hover:text-white/80">
            Frequently Asked Questions
          </AccordionTrigger>
          <AccordionContent className="text-white/70">
            {getFaqContent(stepIndex)}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      
      {/* Empty flex-grow div to push navigation buttons to bottom */}
      <div className="flex-grow"></div>
    </motion.div>
  );
};

// Helper function to get FAQ content based on step
function getFaqContent(stepIndex: number): JSX.Element {
  switch(stepIndex) {
    case 0: // Welcome
      return (
        <div className="space-y-2">
          <p><strong>Q: How long does the simulation take to complete?</strong><br/>
          A: A complete career path typically takes 30-60 minutes, depending on your choices and game speed settings.</p>
          <p><strong>Q: Can I save my progress?</strong><br/>
          A: Yes, the simulation auto-saves your progress (if enabled in settings).</p>
        </div>
      );
    case 1: // Choose career path
      return (
        <div className="space-y-2">
          <p><strong>Q: Can I change my role later?</strong><br/>
          A: You cannot change your initial role, but you can specialize in different paths as you advance.</p>
          <p><strong>Q: Which role is best for beginners?</strong><br/>
          A: Hardware Technician and Operations Manager roles have more straightforward progression paths.</p>
        </div>
      );
    case 2: // Challenges
      return (
        <div className="space-y-2">
          <p><strong>Q: What happens if I make a wrong decision?</strong><br/>
          A: There are no strictly "wrong" decisions - each choice has trade-offs that affect different KPIs.</p>
          <p><strong>Q: How often do challenges appear?</strong><br/>
          A: You'll face approximately 2-4 challenges per simulation year.</p>
        </div>
      );
    case 3: // Skills
      return (
        <div className="space-y-2">
          <p><strong>Q: How many skills can I acquire?</strong><br/>
          A: There's no hard limit, but higher-level skills require prerequisites and career advancement.</p>
          <p><strong>Q: Can skills become obsolete?</strong><br/>
          A: No, once acquired, skills provide permanent benefits to your career.</p>
        </div>
      );
    case 4: // Time management
      return (
        <div className="space-y-2">
          <p><strong>Q: Can I speed up or slow down time?</strong><br/>
          A: Yes, you can adjust simulation speed in the settings menu.</p>
          <p><strong>Q: What happens if the simulation gets stuck?</strong><br/>
          A: Use the "Reset Simulation" button to resolve any issues without losing progress.</p>
        </div>
      );
    case 5: // Career progression
      return (
        <div className="space-y-2">
          <p><strong>Q: What determines when I get promoted?</strong><br/>
          A: Promotions require minimum time in role, experience points, and meeting KPI thresholds.</p>
          <p><strong>Q: Can I be demoted?</strong><br/>
          A: No, career progression only moves forward in the simulation.</p>
        </div>
      );
    case 6: // Settings
      return (
        <div className="space-y-2">
          <p><strong>Q: How does difficulty affect gameplay?</strong><br/>
          A: Higher difficulties increase KPI requirements and make challenges more complex.</p>
          <p><strong>Q: Can I change settings mid-simulation?</strong><br/>
          A: Yes, settings can be adjusted at any time through the settings menu.</p>
        </div>
      );
    case 7: // Winning
      return (
        <div className="space-y-2">
          <p><strong>Q: What happens after I complete the simulation?</strong><br/>
          A: You can view your career summary, achievements, and try again with a different role.</p>
          <p><strong>Q: Is there a time limit to complete the simulation?</strong><br/>
          A: No, you can progress at your own pace through your career.</p>
        </div>
      );
    default:
      return <p>No FAQs for this section.</p>;
  }
}
